import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../common/Button';

const philosophicalQuotes = [
  {
    text: "The unexamined life is not worth living.",
    author: "Socrates",
    context: "This quote reminds us of the importance of self-reflection and critical thinking in our pursuit of wisdom and fulfillment."
  },
  {
    text: "We are what we repeatedly do. Excellence, then, is not an act, but a habit.",
    author: "Aristotle",
    context: "This quote emphasizes the power of habits and consistent practice in developing virtue and excellence."
  },
  {
    text: "No man ever steps in the same river twice, for it's not the same river and he's not the same man.",
    author: "Heraclitus",
    context: "This quote highlights the constant nature of change and the importance of adapting to life's ever-changing circumstances."
  },
  {
    text: "The mind is not a vessel to be filled, but a fire to be kindled.",
    author: "Plutarch",
    context: "This quote emphasizes that true education is about inspiring a love of learning rather than merely accumulating facts."
  },
  {
    text: "Know thyself.",
    author: "Inscription at the Temple of Apollo at Delphi",
    context: "This ancient maxim reminds us that self-knowledge is the foundation of wisdom and personal growth."
  }
];

const dailyChallenges = [
  {
    title: "The Socratic Examination",
    description: "Identify one belief you hold strongly. Now, question it thoroughly. What evidence supports it? What might challenge it? Can you see it from another perspective?",
    difficulty: "Moderate",
    category: "Critical Thinking"
  },
  {
    title: "Stoic Resilience",
    description: "Identify something causing you stress that is beyond your control. Practice accepting it while focusing your energy on what you can change.",
    difficulty: "Challenging",
    category: "Emotional Regulation"
  },
  {
    title: "Aristotle's Golden Mean",
    description: "Consider a virtue (courage, honesty, etc.). Reflect on how this virtue represents a balance between two extremes in your life.",
    difficulty: "Moderate",
    category: "Virtue Ethics"
  }
];

const Personalization = ({ userPreferences, onContinue }) => {
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [isGenerating, setIsGenerating] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      const quoteIndex = Math.floor(Math.random() * philosophicalQuotes.length);
      setSelectedQuote(philosophicalQuotes[quoteIndex]);
      const challengeIndex = Math.floor(Math.random() * dailyChallenges.length);
      setDailyChallenge(dailyChallenges[challengeIndex]);
      setIsGenerating(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [userPreferences]);

  return (
    <motion.div
      className="max-w-3xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="text-center mb-8">
        <h2 className="text-3xl font-serif font-bold text-white mb-4">
          Your Personalized Path
        </h2>
        <p className="text-lg text-white/60">
          Based on your responses, we've crafted your experience.
        </p>
      </div>

      {isGenerating ? (
        <div className="py-16 text-center">
          <div className="flex justify-center space-x-2 mb-6">
            <div className="w-3 h-3 rounded-full bg-oliveGold animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-3 h-3 rounded-full bg-oliveGold animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-3 h-3 rounded-full bg-oliveGold animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          <p className="text-lg font-serif text-oliveGold/60">The Oracle is consulting the ancient wisdom...</p>
        </div>
      ) : (
        <>
          {/* Quote */}
          {selectedQuote && (
            <motion.div
              className="mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <h3 className="text-lg font-serif font-semibold text-oliveGold/80 mb-4">
                Your Philosophical Quote
              </h3>
              <div className="rounded-lg border border-oliveGold/20 overflow-hidden" style={{ background: '#0f1d32' }}>
                <div className="p-6 border-b border-oliveGold/10" style={{ background: 'rgba(197, 165, 114, 0.08)' }}>
                  <blockquote className="italic text-xl font-serif text-oliveGold/90">
                    "{selectedQuote.text}"
                  </blockquote>
                  <p className="mt-4 text-right font-medium text-white/50">— {selectedQuote.author}</p>
                </div>
                <div className="p-6">
                  <p className="text-white/60">{selectedQuote.context}</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Challenge */}
          {dailyChallenge && (
            <motion.div
              className="mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <h3 className="text-lg font-serif font-semibold text-oliveGold/80 mb-4">
                Your First Daily Challenge
              </h3>
              <div className="rounded-lg border border-oracleGreen/20 overflow-hidden" style={{ background: '#0f1d32' }}>
                <div className="p-6 border-b border-oracleGreen/10" style={{ background: 'rgba(57, 114, 94, 0.1)' }}>
                  <h4 className="text-xl font-medium text-oracleGreen">{dailyChallenge.title}</h4>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-oracleGreen/15 text-oracleGreen/80 rounded-full">
                      {dailyChallenge.difficulty}
                    </span>
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-oracleGreen/15 text-oracleGreen/80 rounded-full">
                      {dailyChallenge.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-white/60">{dailyChallenge.description}</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* How it works */}
          <motion.div
            className="mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
          >
            <div className="rounded-lg border border-white/10 p-6" style={{ background: '#0f1d32' }}>
              <h3 className="text-lg font-serif font-semibold text-white mb-4">
                How We'll Personalize Your Experience
              </h3>
              <ul className="list-disc list-inside space-y-2 text-white/50">
                <li>Customize philosophical guidance to match your learning style</li>
                <li>Focus content on your areas of philosophical interest</li>
                <li>Align daily challenges with your current life goals</li>
                <li>Adapt AI interactions to suit your preferences</li>
              </ul>
            </div>
          </motion.div>

          <div className="mt-8 text-center">
            <Button onClick={onContinue} size="lg">
              Continue to Your Oikosystem
            </Button>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default Personalization;
